import React, { useState, useEffect } from "react";
import "../style/css/TelegramBot.css"; // Reuse same CSS
import api from "../api.js";

// TypeScript interfaces
interface GoogleCalendarSettings {
  syncEvents: boolean;
  createEvents: boolean;
  deleteEvents: boolean;
}

interface GoogleCalendarData {
  connected: boolean;
  email: string;
  authLink: string;
  settings: GoogleCalendarSettings;
}

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: GoogleCalendarSettings;
  onSave: (settings: GoogleCalendarSettings) => void;
  email: string;
}

const ConnectedIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M5 12l5 5L20 7" />
  </svg>
);

const DisconnectedIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const EditIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const CalendarIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const SyncIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const CreateIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const DeleteIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M3 6h18v2H3z" />
    <path d="M19 6v14c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2V6m3 0V4c0-.6.4-1 1-1h4c.6 0 1 .4 1 1v2M10 11v6m4-6v6" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

// Edit Modal Component (same structure)
const EditModal: React.FC<EditModalProps> = ({
  isOpen,
  onClose,
  settings,
  onSave,
  email,
}) => {
  const [formSettings, setFormSettings] =
    useState<GoogleCalendarSettings>(settings);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formSettings);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Editar Configuración</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="bot-info">
          <div className="bot-info-icon">
            <CalendarIcon />
          </div>
          <div>
            <h4>{email}</h4>
            <p className="text-muted">
              Configura la sincronización de Google Calendar
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formSettings.syncEvents}
                onChange={(e) =>
                  setFormSettings((prev) => ({
                    ...prev,
                    syncEvents: e.target.checked,
                  }))
                }
              />
              <span className="checkbox-custom"></span>
              <div className="checkbox-text">
                <div className="checkbox-title">
                  <SyncIcon />
                  <span>Sincronizar eventos</span>
                </div>
                <p className="checkbox-description">
                  Mantén tus eventos sincronizados en tiempo real
                </p>
              </div>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formSettings.createEvents}
                onChange={(e) =>
                  setFormSettings((prev) => ({
                    ...prev,
                    createEvents: e.target.checked,
                  }))
                }
              />
              <span className="checkbox-custom"></span>
              <div className="checkbox-text">
                <div className="checkbox-title">
                  <CreateIcon />
                  <span>Crear eventos</span>
                </div>
                <p className="checkbox-description">
                  Permite crear eventos automáticamente desde la app
                </p>
              </div>
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formSettings.deleteEvents}
                onChange={(e) =>
                  setFormSettings((prev) => ({
                    ...prev,
                    deleteEvents: e.target.checked,
                  }))
                }
              />
              <span className="checkbox-custom"></span>
              <div className="checkbox-text">
                <div className="checkbox-title">
                  <DeleteIcon />
                  <span>Eliminar eventos</span>
                </div>
                <p className="checkbox-description">
                  Permite eliminar eventos cancelados automáticamente
                </p>
              </div>
            </label>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn--primary">
              Guardar cambios
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Component (same structure)
const GoogleCalendar: React.FC = () => {
  const [calendarData, setCalendarData] = useState<GoogleCalendarData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchCalendarData();
  }, []);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const response = await api.get("/profiles/profile_google_calendar/");
      const data: GoogleCalendarData = response.data;
      setCalendarData(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    if (calendarData?.authLink) {
      window.open(calendarData.authLink, "_blank", "noopener,noreferrer");
    }
  };

  const handleSaveSettings = (newSettings: GoogleCalendarSettings) => {
    setCalendarData((prev) =>
      prev ? { ...prev, settings: newSettings } : null,
    );
  };

  if (loading) {
    return (
      <div className="telegram-bot-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando configuración de Google Calendar...</p>
        </div>
      </div>
    );
  }

  if (!calendarData) {
    return (
      <div className="telegram-bot-container">
        <div className="error-state">
          <CalendarIcon />
          <p>Error al cargar los datos de Google Calendar</p>
          <button className="btn btn--primary" onClick={fetchCalendarData}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="telegram-bot-container">
      <div className="telegram-bot-card">
        <div className="bot-header">
          <div
            className="bot-icon-wrapper"
            style={{ background: "linear-gradient(135deg, #4285F4, #34A853)" }}
          >
            <GoogleIcon />
          </div>
          <div>
            <h2>Google Calendar</h2>
            <div className="status-badge">
              {calendarData.connected ? (
                <>
                  <ConnectedIcon />
                  <span className="status-connected">
                    Conectado a {calendarData.email}
                  </span>
                </>
              ) : (
                <>
                  <DisconnectedIcon />
                  <span className="status-disconnected">No conectado</span>
                </>
              )}
            </div>
          </div>
        </div>

        {!calendarData.connected ? (
          <div className="connection-prompt">
            <p className="prompt-text">
              Conecta Google Calendar para sincronizar eventos y recibir
              recordatorios automáticos.
            </p>
            {/*<button
              className="btn btn--primary btn--large"
              onClick={handleConnect}
            >
              <GoogleIcon />
              Conectar Google Calendar
            </button>
            <p className="help-text">
              Se abrirá Google para autorizar el acceso. Permite todos los
              permisos necesarios.
            </p>*/}
          </div>
        ) : (
          <div className="bot-settings">
            <div className="settings-header">
              <h3>Configuración de sincronización</h3>
              <button
                className="btn btn--secondary btn--small"
                onClick={() => setIsModalOpen(true)}
              >
                <EditIcon />
                Editar configuración
              </button>
            </div>

            <div className="settings-list">
              <div
                className={`setting-item ${
                  calendarData.settings.syncEvents ? "active" : "inactive"
                }`}
              >
                <div className="setting-icon">
                  <SyncIcon />
                </div>
                <div className="setting-content">
                  <h4>Sincronizar eventos</h4>
                  <p>Mantén tus eventos sincronizados en tiempo real</p>
                </div>
                <div className="setting-status">
                  <span
                    className={`status-dot ${
                      calendarData.settings.syncEvents ? "on" : "off"
                    }`}
                  ></span>
                  {calendarData.settings.syncEvents
                    ? "Activado"
                    : "Desactivado"}
                </div>
              </div>

              <div
                className={`setting-item ${
                  calendarData.settings.createEvents ? "active" : "inactive"
                }`}
              >
                <div className="setting-icon">
                  <CreateIcon />
                </div>
                <div className="setting-content">
                  <h4>Crear eventos</h4>
                  <p>Permite crear eventos automáticamente desde la app</p>
                </div>
                <div className="setting-status">
                  <span
                    className={`status-dot ${
                      calendarData.settings.createEvents ? "on" : "off"
                    }`}
                  ></span>
                  {calendarData.settings.createEvents
                    ? "Activado"
                    : "Desactivado"}
                </div>
              </div>

              <div
                className={`setting-item ${
                  calendarData.settings.deleteEvents ? "active" : "inactive"
                }`}
              >
                <div className="setting-icon">
                  <DeleteIcon />
                </div>
                <div className="setting-content">
                  <h4>Eliminar eventos</h4>
                  <p>Permite eliminar eventos cancelados automáticamente</p>
                </div>
                <div className="setting-status">
                  <span
                    className={`status-dot ${
                      calendarData.settings.deleteEvents ? "on" : "off"
                    }`}
                  ></span>
                  {calendarData.settings.deleteEvents
                    ? "Activado"
                    : "Desactivado"}
                </div>
              </div>
            </div>
          </div>
        )}

        <EditModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          settings={calendarData.settings}
          onSave={handleSaveSettings}
          email={calendarData.email}
        />
      </div>
    </div>
  );
};

export default GoogleCalendar;
